/** Wer ist angemeldet. Eine Quelle fuer die ganze Oberflaeche. */
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type Sitzungsauskunft } from './api'

interface Anmeldelage {
  auskunft: Sitzungsauskunft | null
  laedt: boolean
  /** Die letzte Sitzung ist abgelaufen; die Anmeldeseite sagt das dazu. */
  abgelaufen: boolean
  anmelden: (email: string, passwort: string) => Promise<void>
  abmelden: () => Promise<void>
}

const Zusammenhang = createContext<Anmeldelage | null>(null)

export function AnmeldungAnbieter({ children }: { children: ReactNode }) {
  const abfragen = useQueryClient()
  const [auskunft, setzeAuskunft] = useState<Sitzungsauskunft | null>(null)
  const [laedt, setzeLaedt] = useState(true)
  const [abgelaufen, setzeAbgelaufen] = useState(false)

  // Meldet api.ts eine abgelaufene Sitzung, faellt die Oberflaeche auf die
  // Anmeldung zurueck und vergisst alles Geladene.
  useEffect(() => {
    const beiAblauf = () => {
      setzeAuskunft((bisher) => {
        if (bisher) setzeAbgelaufen(true)
        return null
      })
      abfragen.clear()
    }
    window.addEventListener('sitzung-abgelaufen', beiAblauf)
    return () => window.removeEventListener('sitzung-abgelaufen', beiAblauf)
  }, [abfragen])

  useEffect(() => {
    let abgebrochen = false
    void (async () => {
      try {
        const gelesen = await api.holen<Sitzungsauskunft>('/api/ich')
        if (!abgebrochen) setzeAuskunft(gelesen)
      } catch (fehler) {
        // 401 ist der Normalfall beim ersten Aufruf, kein Grund zur Aufregung.
        if (!abgebrochen && !(fehler instanceof ApiFehler && fehler.status === 401)) {
          console.error(fehler)
        }
      } finally {
        if (!abgebrochen) setzeLaedt(false)
      }
    })()
    return () => {
      abgebrochen = true
    }
  }, [])

  const anmelden = useCallback(async (email: string, passwort: string) => {
    await api.senden('/api/anmeldung', { email, passwort })
    setzeAuskunft(await api.holen<Sitzungsauskunft>('/api/ich'))
    setzeAbgelaufen(false)
  }, [])

  const abmelden = useCallback(async () => {
    try {
      await api.senden('/api/abmeldung')
    } finally {
      setzeAuskunft(null)
      // Am geteilten Kanzlei-PC sieht die naechste Person nicht die Listen der vorherigen.
      abfragen.clear()
    }
  }, [abfragen])

  return <Zusammenhang.Provider value={{ auskunft, laedt, abgelaufen, anmelden, abmelden }}>{children}</Zusammenhang.Provider>
}

export function useAnmeldung(): Anmeldelage {
  const lage = useContext(Zusammenhang)
  if (!lage) throw new Error('useAnmeldung braucht den AnmeldungAnbieter darum herum.')
  return lage
}
